<script setup lang="ts">
// MDS UI components import
import "@maersk-global/mds-components-core-table";
import "@maersk-global/mds-components-core-select";
import "@maersk-global/mds-components-core-option";
import "@maersk-global/mds-components-core-pagination";
// Vue imports
import { computed, ref, watch } from "vue";

import type { PaginationAttributes } from "@/interface/pagination_interface";
import ContainerPrintView from "@/components/common/ContainerPrintView.vue";
import ShimmerLoaderTableView from "@/components/common/ShimmerLoaderTableView.vue";
import { useExportBookingDetailStore } from "@/store/export/export_asset_store";
import ExportContainerTableView from "@/components/export/ExportContainersTableView.vue";
import type { ValidBookingIds } from "@/interface/export/export_booking_detail_interface";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useExportLocationStore } from "@/store/export/export_location_store";
import { useGlobalStore } from "@/store/global";
import { appButtonClick } from "@/utils/analytics";

const locationStore = useExportLocationStore();
const globalStore = useGlobalStore();
const terminalConfigurationStore = useTerminalConfigurationStore();
const exportBookingDetailStore = useExportBookingDetailStore();
const rawContainer = computed((): ValidBookingIds[] => exportBookingDetailStore.originalContainers);
const rawBookings = computed((): ValidBookingIds[] => exportBookingDetailStore.originalBookings);

const filterContainer = computed((): ValidBookingIds[] => exportBookingDetailStore.filteredExportAsset);
const filterBooking = computed((): ValidBookingIds[] => exportBookingDetailStore.filterBooking);
const isContainerSelected = computed((): boolean => exportBookingDetailStore.isContainer);

const loader = computed((): boolean => exportBookingDetailStore.isLoading);
const errorCode = computed((): string => exportBookingDetailStore.error || "");
const currentPage = ref<number>(1);
const searchInput = ref<string>("");

// Pagination
const containerPagination = ref<PaginationAttributes>({
  totalPages: 0,
  page: 1,
  pageSize: 25,
  totalRecords: 0
});

const bookingPagination = ref<PaginationAttributes>({
  totalPages: 0,
  page: 1,
  pageSize: 25,
  totalRecords: 0
});

const labels = {
  exportIdLoader: globalStore.sitecoreData.common.export_loader,
  exportResult: globalStore.sitecoreData.headings.export_enquiry_result,
  csv: globalStore.sitecoreData.action_button.export_csv,
  print: globalStore.sitecoreData.action_button.print,
  resetBillOfLadingFilter: globalStore.sitecoreData.action_button.bolFilter,
  searchIds: globalStore.sitecoreData.common.export_assets_search,
  noResult: globalStore.sitecoreData.search.no_search_result,
  filterBOL: globalStore.sitecoreData.search.filter_bol,
  thirdShiftAppointment: ""
};

// Change of booking numbers
const toggleBookingIds = (): void => {
  appButtonClick("Export Enquiry", "Show Container ID", locationStore.anayticsTerminalDetails.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  exportBookingDetailStore.removeBookingNumberFilter();
  exportBookingDetailStore.toggleContainer();
};

const getPageCount = computed((): number => {
  return isContainerSelected.value ? containerPagination.value.totalPages : bookingPagination.value.totalPages;
});

// Page change
const paginationChange = (event: any): void => {
  appButtonClick("Export Enquiry", "Pagination Click", locationStore.anayticsTerminalDetails.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  currentPage.value = event.detail;
};

const getTerminalSpecificBookingLabel = (fieldName: string): string => {
  const selectedOneStreamCode = locationStore.anayticsTerminalDetails.oneStreamCode;

  if (globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.export?.booking_columns) {
    return (
      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.export.booking_columns[
        fieldName
      ] || globalStore.sitecoreData.table_columns.export.booking_columns[fieldName]
    );
  }
  return globalStore.sitecoreData.table_columns.export.booking_columns[fieldName];
};

const getTerminalSpecificContainerLabel = (fieldName: string): string => {
  const selectedOneStreamCode = locationStore.anayticsTerminalDetails.oneStreamCode;

  if (globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.export?.container_columns) {
    return (
      globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.table_columns?.export.container_columns[
        fieldName
      ] || globalStore.sitecoreData.table_columns.export.container_columns[fieldName]
    );
  }
  return globalStore.sitecoreData.table_columns.export.container_columns[fieldName];
};
// Container table column name
const containerColumnNames = computed((): any => {
  return terminalConfigurationStore.exportConfiguration.containerColumnNames
    .filter((item: any): any => item.enabled) // Filter items where enabled is true
    .map((item: any) => {
      const fieldName = `${item.id}`;
      return {
        ...item,
        label: getTerminalSpecificContainerLabel(fieldName),
        width: fieldName.includes("Date") ? "150px" : "140px"
      };
    });
});

// Booking table column name
const bookingColumnNames = computed((): any => {
  return terminalConfigurationStore.exportConfiguration.bookingColumnNames
    .filter((item: any): any => item.enabled) // Filter items where enabled is true
    .map((item: any) => {
      const fieldName = `${item.id}`;
      return {
        ...item,
        label: getTerminalSpecificBookingLabel(fieldName),
        width: fieldName.includes("Date") ? "150px" : "140px"
      };
    });
});

const searchAssets = (): void => {
  const searchText = searchInput.value.trim().toLowerCase();
  if (isContainerSelected.value) {
    exportBookingDetailStore.searchContainerFilterAssets(searchText);
  } else {
    exportBookingDetailStore.searchBookingFilterAssets(searchText);
  }
};

watch(
  (): any => exportBookingDetailStore.containerPagination,
  (data: PaginationAttributes): void => {
    containerPagination.value = { ...data };
  },
  { immediate: true, deep: true }
);

watch(
  (): any => exportBookingDetailStore.bookingPagination,
  (data: PaginationAttributes): void => {
    bookingPagination.value = { ...data };
  },
  { immediate: true, deep: true }
);

const getShowContainerLabel = () => {
  const selectedOneStreamCode = locationStore.anayticsTerminalDetails.oneStreamCode;
  const label =
    globalStore.sitecoreData.terminal_specific[selectedOneStreamCode]?.action_button?.show_container_id ||
    globalStore.sitecoreData.action_button.show_container_id;
  return label;
};

const getBookingNumbers = () => {
  const bookingNumbers = filterBooking.value
    .filter((item) => item.isContainer === false)
    .map((item) => item.bookingNumber);

  return bookingNumbers;
};
// Change of booking numbers
const changeBookingNumber = (e: Event): void => {
  appButtonClick("Export Enquiry", "Select BOL dropdown clicks", locationStore.anayticsTerminalDetails.oneStreamCode, {
    ...locationStore.anayticsTerminalDetails
  });
  const target = e.target as HTMLSelectElement;
  const bookingNumber: string = typeof target.value === "string" ? target.value.trim() : target.value;
  if (bookingNumber !== "") {
    exportBookingDetailStore.filterByBookingNumber(bookingNumber);
  } else {
    exportBookingDetailStore.removeBookingNumberFilter();
  }
};
const showthirdShiftSection = () => {
  if (locationStore.terminalSelected.oneStreamCode === "US005U") {
    labels.thirdShiftAppointment =
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.export
        ?.third_shift_appointment || [];
  }
  return locationStore.terminalSelected.oneStreamCode === "US005U";
};
const showAlertNotification = () => {
  if (globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message) {
    return (
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message &&
      globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode].alert_message.message !==
        ""
    );
  }
  return false;
};
</script>

<template>
  <div v-if="showAlertNotification()" class="mrgn-btm-10">
    <mc-notification
      heading=""
      :body="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.message
      "
      :appearance="
        globalStore.sitecoreData.terminal_specific[locationStore.terminalSelected.oneStreamCode]?.alert_message.category
      "
    >
    </mc-notification>
  </div>
  <div class="">
    <div v-if="loader" class="search-card">
      <ShimmerLoaderTableView :msg="labels.exportIdLoader"></ShimmerLoaderTableView>
    </div>
    <div v-if="!errorCode && (rawContainer.length != 0 || rawBookings.length !== 0) && !loader">
      <div class="page-header">
        <h3>{{ labels.exportResult }} ({{ isContainerSelected ? filterContainer.length : filterBooking.length }})</h3>
      </div>
      <div class="search-card">
        <div v-if="showthirdShiftSection()" class="group_code_section">
          <span>{{ labels.thirdShiftAppointment }}</span>
        </div>
        <div class="devider"></div>
        <div class="container-action-buttons">
          <div class="container-action-buttons__search">
            <div style="margin: 10px 0">
              <mc-checkbox
                :checked="isContainerSelected"
                :label="getShowContainerLabel()"
                @change="toggleBookingIds()"
              />
            </div>
            <div v-if="isContainerSelected">
              <mc-select
                label=""
                :placeholder="labels.filterBOL"
                icon="file-magnifying-glass"
                name="billOfLadingNumber"
                clearbutton="true"
                @optionselected="changeBookingNumber($event)"
                @clearbuttonclick="changeBookingNumber($event)"
              >
                <mc-option v-for="booking in getBookingNumbers()" :key="booking" :value="booking">
                  {{ booking }}
                </mc-option>
              </mc-select>
            </div>

            <mc-input
              v-if="isContainerSelected"
              v-model="searchInput"
              style="width: 40%"
              icon="magnifying-glass"
              label=""
              :placeholder="labels.searchIds"
              name="username"
              @input="searchAssets"
            />
          </div>
          <ContainerPrintView
            :containers="isContainerSelected ? filterContainer : filterBooking"
            :fileName="'Export-Booking'"
            :headerLabel="isContainerSelected ? containerColumnNames : bookingColumnNames"
            :is-disabled="isContainerSelected ? !filterContainer.length : !filterBooking.length"
            :terminalOneStreamCode="locationStore.anayticsTerminalDetails.oneStreamCode"
            :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
          />
        </div>

        <div v-if="filterContainer.length > 0 || filterBooking.length > 0">
          <ExportContainerTableView
            :columnNames="isContainerSelected ? containerColumnNames : bookingColumnNames"
            :assets="isContainerSelected ? filterContainer : filterBooking"
            :currentPage="currentPage"
            :pagination="isContainerSelected ? containerPagination : bookingPagination"
            :showHoldsExplanation="locationStore.anayticsTerminalDetails.oneStreamCode === 'PE018U'"
            :showHoldsTable="locationStore.anayticsTerminalDetails.oneStreamCode === 'IN103L'"
            :totalpages="getPageCount"
            :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
            :isContainerSelected="isContainerSelected"
            @pagechange="paginationChange"
          />
        </div>
        <div v-else>
          <ExportContainerTableView
            :columnNames="isContainerSelected ? containerColumnNames : bookingColumnNames"
            :assets="[]"
            :currentPage="currentPage"
            :pagination="isContainerSelected ? containerPagination : bookingPagination"
            :sortColumnId="isContainerSelected ? 'isReadyForLoad' : ''"
            :showHoldsExplanation="locationStore.anayticsTerminalDetails.oneStreamCode === 'PE018U'"
            :showHoldsTable="locationStore.anayticsTerminalDetails.oneStreamCode === 'IN103L'"
            :totalpages="getPageCount"
            :analyticsTerminalDetails="locationStore.anayticsTerminalDetails"
            @pagechange="paginationChange"
          />
        </div>

        <!-- Pagination for export enquiry data-->
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.container-action-buttons {
  display: flex;
  gap: 25px;
}
</style>
